import type { Block, Sketch } from './types'

/**
 * Tegner en skitse af en skærm ud fra byggeklodserne i vejledningen.
 *
 * Bevidst en skitse og ikke et skærmbillede: den bliver ikke forkert, hver gang et
 * felt flytter sig lidt, den er skarp på telefonen, og der står ingen rigtige
 * kundedata på den. Tallene i de orange cirkler passer til trinene ved siden af.
 */
export function Wireframe({ sketch }: { sketch: Sketch }) {
  return (
    <figure className={`wf wf--${sketch.device}`} aria-label={`Skitse${sketch.title ? `: ${sketch.title}` : ''}`}>
      <div className="wf__screen">
        {sketch.device === 'desktop' && (
          <div className="wf__sidebar" aria-hidden>
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
        )}
        <div className="wf__body">
          {sketch.title && <div className="wf__top">{sketch.title}</div>}
          <div className="wf__content">
            {sketch.blocks.map((block, i) => (
              <WfBlock key={i} block={block} />
            ))}
          </div>
          {/* Bundmenuen tegnes i samme raekkefoelge som i appen (navigation.ts) -
              tegningen skal vise det, brugeren faktisk har for sig. */}
          {sketch.device === 'phone' && sketch.nav && (
            <div className="wf__nav" aria-hidden>
              <span>🏠</span>
              <span>📣</span>
              <span>📝</span>
              <span>📅</span>
              <span>📁</span>
              <span>📷</span>
              <span>☰</span>
            </div>
          )}
        </div>
      </div>
      {sketch.caption && <figcaption className="tiny muted wf__caption">{sketch.caption}</figcaption>}
    </figure>
  )
}

export function Num({ n }: { n?: number }) {
  if (!n) return null
  return (
    <span className="wf-n" aria-label={`Punkt ${n}`}>
      {n}
    </span>
  )
}

function Item({ n, className, children }: { n?: number; className?: string; children: React.ReactNode }) {
  return (
    <div className="wf-item">
      <div className={className}>{children}</div>
      <Num n={n} />
    </div>
  )
}

function WfBlock({ block }: { block: Block }) {
  switch (block.t) {
    case 'title':
      return (
        <Item n={block.n} className="wf-title">
          <div>{block.text}</div>
          {block.sub && <div className="wf-sub">{block.sub}</div>}
        </Item>
      )
    case 'text':
      return (
        <Item n={block.n} className="wf-text">
          {block.text}
        </Item>
      )
    case 'btn':
      return (
        <Item
          n={block.n}
          className={`wf-btn${block.primary ? ' wf-btn--primary' : ''}${block.danger ? ' wf-btn--danger' : ''}`}
        >
          {block.text}
        </Item>
      )
    case 'btns':
      return (
        <div className="wf-btns">
          {block.items.map((item, i) => (
            <span
              key={i}
              className={`wf-btn${item.primary ? ' wf-btn--primary' : ''}${item.danger ? ' wf-btn--danger' : ''}`}
            >
              {item.text}
              <Num n={item.n} />
            </span>
          ))}
        </div>
      )
    case 'field':
      return (
        <Item n={block.n} className="wf-field">
          <div className="wf-field__label">{block.label}</div>
          <div className="wf-field__box">{block.value ?? ''}</div>
        </Item>
      )
    case 'check':
      return (
        <Item n={block.n} className="wf-check">
          <span className={`wf-check__box${block.checked ? ' wf-check__box--on' : ''}`}>{block.checked ? '✓' : ''}</span>
          {block.text}
        </Item>
      )
    case 'pills':
      return (
        <Item n={block.n}>
          {block.items.map((item, i) => (
            <span key={i} className="wf-pill">
              {item}
            </span>
          ))}
        </Item>
      )
    case 'bar':
      return (
        <Item n={block.n}>
          {block.label && <div className="wf-sub">{block.label}</div>}
          <div className="wf-bar">
            <span style={{ width: `${Math.max(0, Math.min(100, block.value))}%` }} />
          </div>
        </Item>
      )
    case 'row':
      return (
        <Item n={block.n} className="wf-row">
          <div style={{ minWidth: 0 }}>
            <div className="wf-row__text">{block.text}</div>
            {block.meta && <div className="wf-sub">{block.meta}</div>}
          </div>
          {block.right && <span className="wf-row__right">{block.right}</span>}
        </Item>
      )
    case 'code':
      return (
        <Item n={block.n} className="wf-code">
          {block.text}
        </Item>
      )
    case 'banner':
      return (
        <Item n={block.n} className={`wf-banner wf-banner--${block.tone ?? 'info'}`}>
          {block.text}
        </Item>
      )
    case 'photo':
      return (
        <Item n={block.n} className="wf-photo">
          {block.label}
        </Item>
      )
    case 'table':
      return (
        <Item n={block.n} className="wf-table">
          <div className="wf-table__head">
            {block.cols.map((col) => (
              <span key={col}>{col}</span>
            ))}
          </div>
          {Array.from({ length: block.rows ?? 3 }).map((_, i) => (
            <div key={i} className="wf-table__row">
              {block.cols.map((col) => (
                <span key={col} />
              ))}
            </div>
          ))}
        </Item>
      )
    case 'card':
      return (
        <div className="wf-item">
          <div className="wf-card">
            {block.title && <div className="wf-card__title">{block.title}</div>}
            {block.children.map((child, i) => (
              <WfBlock key={i} block={child} />
            ))}
          </div>
          <Num n={block.n} />
        </div>
      )
    case 'cols':
      return (
        <div className="wf-cols">
          <div className="wf-col">
            {block.left.map((child, i) => (
              <WfBlock key={i} block={child} />
            ))}
          </div>
          <div className="wf-col">
            {block.right.map((child, i) => (
              <WfBlock key={i} block={child} />
            ))}
          </div>
        </div>
      )
    case 'qr':
      return (
        <Item n={block.n} className="wf-qrwrap">
          <span className="wf-qr" aria-hidden />
          {block.label && <span className="wf-sub">{block.label}</span>}
        </Item>
      )
    case 'sign':
      return (
        <Item n={block.n} className="wf-sign">
          <svg viewBox="0 0 120 30" aria-hidden>
            <path d="M5 22 C 20 5, 30 28, 45 14 S 70 6, 80 20 S 105 10, 115 16" fill="none" stroke="currentColor" strokeWidth="2" />
          </svg>
        </Item>
      )
  }
}
